import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/padel-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "FX", title: "League fixture scheduling", desc: "Create weekly fixtures, matchdays and recurring schedules for your league."},
    {code: "DV", title: "Divisions and groups", desc: "Organize players or teams into divisions, groups, levels or categories."},
    {code: "TB", title: "League tables", desc: "Track points, wins, losses, draws, sets, games or custom scoring rules."},
    {code: "SC", title: "Score collection", desc: "Keep results centralized instead of scattered across messages and paper."},
    {code: "RK", title: "Player rankings", desc: "Publish player or team rankings throughout the season."},
    {code: "SS", title: "Season management", desc: "Run recurring leagues across weeks, months or club seasons."},
];

const LEAGUE_TYPES = [
    {name: "Ladder Leagues", sub: "Ongoing rankings and challenges", to: "/ladder-league-generator/"},
    {name: "Round Robin Leagues", sub: "Everyone plays everyone", to: "/round-robin-tournament-generator/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
    {name: "Padel Tournaments", sub: "One-day and event formats", to: "/padel-tournament-software/"},
];

const BENEFITS = [
    {title: "Manage weekly fixtures", desc: "Plan matches across rounds, courts and dates."},
    {title: "Track league standings", desc: "Keep tables up to date as scores are submitted."},
    {title: "Support multiple divisions", desc: "Run beginner, intermediate, advanced or team-based divisions."},
];

const USE_CASES = [
    {title: "For Padel Clubs", desc: "Run weekly leagues, divisions and member competitions."},
    {title: "For League Organizers", desc: "Manage fixtures, results, tables and rankings over a season."},
    {title: "For Social Groups", desc: "Keep regular competitions easy to follow and simple to manage."},
    {title: "For Competitive Divisions", desc: "Separate players by level and track progress over time."},
    {title: "For Ladder Competitions", desc: "Let players challenge others and move up or down the rankings."},
    {title: "For Seasonal Events", desc: "Run competitions over several weeks or months with clear standings."},
];

const COMPARE = [
    {need: "Weekly fixtures", manual: "Manual schedule updates", scorepal: "Structured league fixture management"},
    {need: "Divisions", manual: "Separate tabs and manual sorting", scorepal: "Organized divisions and groups"},
    {need: "Score collection", manual: "Messages, paper or manual entry", scorepal: "Centralized score tracking"},
    {need: "League standings", manual: "Manual formulas and corrections", scorepal: "Clear tables, rankings and results"},
    {need: "Player experience", manual: "Players ask organizers for updates", scorepal: "Fixtures and standings are easier to follow"},
];

const FAQS = [
    {
        q: 'What is padel league software?',
        a: <>Padel league software helps clubs and organizers create fixtures, manage divisions, record scores, update standings and run recurring padel competitions.</>,
    },
    {
        q: 'Can ScorePal manage padel league standings?',
        a: <>Yes. ScorePal can help manage standings, results, rankings, divisions and fixtures across a league season.</>,
    },
    {
        q: 'Can ScorePal support divisions?',
        a: <>Yes. ScorePal can support different groups, levels or divisions for clubs running multiple league categories.</>,
    },
    {
        q: 'Can ScorePal run ladder leagues?',
        a: <>Yes. ScorePal supports <Link to={"/ladder-league-generator/" as any} className="text-[#082F8A] underline font-semibold">ladder-style competitions</Link> where players or teams move up and down rankings over time.</>,
    },
    {
        q: 'Is this different from padel tournament software?',
        a: <>Yes. Padel league software is focused on recurring fixtures, divisions, standings and seasons. <Link to={"/padel-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Padel tournament software</Link> is usually focused on one-day events, brackets, Americano, Mexicano and knockout formats.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Padel League Software for Clubs & Organizers | ScorePal</title>
            <meta name="description" content="Run padel leagues with ScorePal. Create fixtures, manage divisions, track scores, publish standings, manage rankings and run recurring padel competitions from one platform." />
            <link rel="canonical" href={`${config.publicUrl}/padel-league-software/`} />
            <meta property="og:title" content="Padel League Software | ScorePal" />
            <meta property="og:description" content="Create padel league fixtures, manage divisions, track scores and publish standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/padel-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/padel-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Padel League Software | ScorePal" />
            <meta name="twitter:description" content="Run padel leagues, standings, fixtures and rankings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Padel League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Padel league software for creating fixtures, managing divisions, tracking scores, publishing standings and running recurring competitions.",
                "url": "https://scorepal.co/padel-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is padel league software?", "acceptedAnswer": {"@type": "Answer", "text": "Padel league software helps clubs and organizers create fixtures, manage divisions, collect scores, publish league standings and run recurring padel competitions."}},
                    {"@type": "Question", "name": "Can ScorePal manage padel league standings?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can help manage padel league standings, scores, rankings, fixtures and results across a season."}},
                    {"@type": "Question", "name": "Can ScorePal support divisions?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can support different groups, levels or divisions for clubs running multiple league categories."}},
                    {"@type": "Question", "name": "Can ScorePal run ladder leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can support ladder-style padel leagues where players or teams move up and down rankings over time."}},
                    {"@type": "Question", "name": "Is this different from padel tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Padel league software is focused on recurring fixtures, divisions, standings and seasons, while padel tournament software is usually focused on one-day events, brackets, Americano, Mexicano and knockout formats."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Padel League Software", item: "https://scorepal.co/padel-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Padel League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Run padel leagues without spreadsheets
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps padel clubs and organizers create league fixtures, manage divisions, collect
                                    scores, publish standings and track player rankings across weeks, seasons and recurring competitions.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/padel-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live league table card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live league table</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Keep standings clear as results come in across divisions, teams and rounds.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    {[
                                        {pos: 1, team: "Team Wilson", pts: "18 pts"},
                                        {pos: 2, team: "Padel Crew", pts: "15 pts"},
                                        {pos: 3, team: "Smash Club", pts: "12 pts"},
                                        {pos: 4, team: "Court Kings", pts: "9 pts"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[32px_1fr_auto] items-center gap-3 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.team}</strong>
                                            <span className="font-inter text-white/70 text-sm">{row.pts}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to manage a padel league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace manual league tables, WhatsApp score updates and spreadsheet schedules with one platform for fixtures, standings and results.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of padel league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Whether you run a weekly social league, competitive divisions or a ladder-style ranking system, ScorePal helps keep everything organized.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Recurring competitions + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Designed for recurring padel competitions
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Padel leagues need more than a one-off fixture list. You need standings, weekly results, clear
                            divisions and a simple way for players to follow the season.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Division A — Week 5", r: "Active", badge: true},
                                {l: "Next match", r: "Team Wilson vs Padel Crew"},
                                {l: "Completed", r: "18 / 24 matches"},
                                {l: "Top player", r: "Alex Morgan"},
                                {l: "Standings", r: "Updated"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for padel leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Spreadsheets become harder to manage as fixtures, scores, divisions and league tables change week after week.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for clubs, organizers and recurring competitions
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        ScorePal works for padel clubs that need a simple way to keep regular competition organized and visible.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a one-day padel tournament?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is focused on recurring padel leagues. If you are running a one-day event — Americano,
                            Mexicano, King of the Court or a knockout tournament — see our padel tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/padel-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament tools</h3>
                        <ul className="space-y-2 font-inter text-sm text-white/85">
                            <li><Link to={"/americano-generator/" as any} className="hover:underline">Americano generator</Link></li>
                            <li><Link to={"/mexicano-tournament-generator/" as any} className="hover:underline">Mexicano generator</Link></li>
                            <li><Link to={"/king-of-the-court-generator/" as any} className="hover:underline">King of the Court generator</Link></li>
                            <li><Link to={"/round-robin-tournament-generator/" as any} className="hover:underline">Round robin generator</Link></li>
                            <li><Link to={"/group-and-knockout-generator/" as any} className="hover:underline">Group and knockout generator</Link></li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next padel league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Create fixtures, manage divisions, collect scores and publish standings with padel league software built for modern clubs and organizers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Padel Leagues", "Fixtures", "Divisions", "Standings", "Rankings", "Ladder Leagues"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Padel League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
